'use client';

import React, { useCallback, useId, useState } from 'react';
import { classnames } from '@/utils/classnames/classnames';
import QuickOrderDesktop from '@/components/organisms/quick-order/quick-order-desktop';
import QuickOrderUpload from '@/components/organisms/quick-order-upload';
import useCustomRouter from '@/hooks/useCustomRouter';
import { usePathname } from '@/i18n/routing';
import RegisterTradeAccountTrigger from '@/components/organisms/authentication/register-trade-account-trigger';
import SignInTrigger from '@/components/organisms/authentication/sign-in-trigger';
import useMobileProductsHandoff from './hooks/useMobileProductsHandoff';
import MainBar from './components/main-bar';
import DesktopNav from './components/desktop-nav';
import MobileMenu from './components/mobile-menu';
import ProductsSlideMenu from './components/products-slide-menu';
import { sampleMainNavItems, sampleProductCategories, sampleSecondaryLinks } from './constants/sample-data';
import type { JaycarHeaderProps } from './types';

// Phase 5a — scaffold. Static markup with sample data, ready for
// Phase 5b (commercetools category wiring) and 5c (account / cart
// state). Tastic registration happens in Phase 5d.
const JaycarHeader = ({
  logo,
  logoLink,
  mainNavItems = sampleMainNavItems,
  productCategories = sampleProductCategories,
  cartItems = 0,
  accountHref = '/account',
  listsHref = '/purchase-lists',
  cartHref = '/cart',
  className = '',
  belowMainBar,
  secondaryLinks = sampleSecondaryLinks,
  supportContact,
  onSkuLookup,
  resolveSkus,
  backOrdersAllowed,
  paymentTerms,
  searchProducts,
  parseUploadFile,
  addItem,
  csvProducts = [],
  csvProductsLoading = false,
  handleSKUsUpdate,
}: JaycarHeaderProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsMenuOpen, setProductsMenuOpen] = useState(false);
  const [quickOrderOpen, setQuickOrderOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);

  const { requestProductsFromMobile, onMobileDrawerAfterLeave } = useMobileProductsHandoff({
    closeMobileMenu: () => setMobileMenuOpen(false),
    openProductsMenu: () => setProductsMenuOpen(true),
  });

  // One stable id shared between the "Shop Products" triggers (via
  // aria-controls) and the products dialog panel (its id).
  const productsMenuId = useId();

  const router = useCustomRouter();
  const pathname = usePathname();

  // When a quick-order drawer adds to cart while the user is on /cart, the
  // server-rendered cart page needs an explicit refresh — SWR mutates the
  // shared cart cache but doesn't re-run server data fetches.
  const addItemWithCartRefresh = useCallback(
    async (lineItems: Array<{ sku: string; count: number }>) => {
      const result = (await addItem?.(lineItems)) ?? {};
      if (pathname === '/cart') router.refresh();
      return result;
    },
    [addItem, pathname, router],
  );

  return (
    <header className={classnames('w-full', className)}>
      <MainBar
        logo={logo}
        logoLink={logoLink}
        cartItems={cartItems}
        accountHref={accountHref}
        listsHref={listsHref}
        cartHref={cartHref}
        onMobileMenuOpen={() => setMobileMenuOpen(true)}
        onQuickOrderOpen={() => setQuickOrderOpen(true)}
        onBulkUploadOpen={() => setBulkUploadOpen(true)}
      />
      <QuickOrderDesktop
        items={[]}
        csvProducts={csvProducts}
        csvProductsLoading={csvProductsLoading}
        downloadLink="/template.csv"
        addItem={addItemWithCartRefresh}
        handleSKUsUpdate={handleSKUsUpdate}
        onSkuLookup={onSkuLookup}
        backOrdersAllowed={backOrdersAllowed}
        paymentTerms={paymentTerms}
        isOpen={quickOrderOpen}
        onOpen={() => setQuickOrderOpen(true)}
        onClose={() => setQuickOrderOpen(false)}
      />
      <QuickOrderUpload
        downloadLink="/template.csv"
        addItem={addItemWithCartRefresh}
        resolveSkus={resolveSkus}
        backOrdersAllowed={backOrdersAllowed}
        paymentTerms={paymentTerms}
        searchProducts={searchProducts}
        parseUploadFile={parseUploadFile}
        isOpen={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
      />

      {belowMainBar}

      <DesktopNav
        mainNavItems={mainNavItems}
        cta={<RegisterTradeAccountTrigger variant="desktop" />}
        onProductsMenuToggle={() => setProductsMenuOpen((v) => !v)}
        productsMenuOpen={productsMenuOpen}
        productsMenuId={productsMenuId}
      />

      <MobileMenu
        open={mobileMenuOpen}
        mainNavItems={mainNavItems}
        onClose={() => setMobileMenuOpen(false)}
        onShopProducts={requestProductsFromMobile}
        afterLeave={onMobileDrawerAfterLeave}
        authActions={
          <>
            <SignInTrigger />
            <RegisterTradeAccountTrigger variant="mobile" />
          </>
        }
        productsMenuOpen={productsMenuOpen}
        productsMenuId={productsMenuId}
        supportContact={supportContact}
      />

      <ProductsSlideMenu
        open={productsMenuOpen}
        productCategories={productCategories}
        secondaryLinks={secondaryLinks}
        onClose={() => setProductsMenuOpen(false)}
        id={productsMenuId}
      />
    </header>
  );
};

export default JaycarHeader;
